import { Fragment, useMemo, useState, useRef } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  TableSortLabel,
  Box,
} from '@mui/material';
import { useDateSort } from '../../../hooks/useDateSort';
import TableActionsMenu from '../../../components/common/TableActionsMenu';
import { OfferwallMetrics } from '../../../types/offerwall.types';
import type { Dayjs } from 'dayjs';

type Props = {
  data: OfferwallMetrics;
  activeSubTab: number;
  selectedApp?: string | null;
  startDate: Dayjs;
  endDate: Dayjs;
};

const networkNameMap = {
  flex: 'Flex',
  buzzvil: 'Buzzvil',
  ive: 'IVE',
  pincrux: 'Pincrux',
  nasmedia: 'Nasmedia',
  igaworks: 'IGAWorks',
  gpa: 'GPA',
  ADForus: 'Adforus',
  Quantumbit2: 'Quantumbit',
  KEYWORDLAB: 'Keywordlab',
  nas: 'NAS',
  MindSoftLab: 'MindSoftLab',
  OHC: 'OHC',
};

export default function OfferwallPublisherTable({
  data,
  activeSubTab,
  selectedApp,
  startDate,
  endDate,
}: Props) {
  const rowsPerPage = 31;
  const [page, setPage] = useState(0);
  const { sorted: sortedData, sortDirection, toggleSort } = useDateSort(data.topCountOfferwall);

  // 테이블 ref - 엑셀 다운로드용
  const tableRef = useRef<HTMLTableElement>(null);

  // 파일명 생성
  const filename = useMemo(() => {
    const appPart = selectedApp && selectedApp !== '앱 전체' ? `_${selectedApp}` : '_전체';
    return `${startDate.format('YYMMDD')}-${endDate.format('YYMMDD')}${appPart}`;
  }, [startDate, endDate, selectedApp]);

  const paged = useMemo(
    () => sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sortedData, page],
  );

  // 엑셀 다운로드용 전체 데이터 (현재 정렬 상태 그대로, 페이지네이션 무시)
  const allRowsDataForExcel = useMemo(() => {
    return sortedData.map((day) => {
      return [
        day.date,
        `${day.title} (${day.reward}캐시)`,
        day.count.toLocaleString(),
        networkNameMap[day.networkName as keyof typeof networkNameMap] || day.networkName,
      ];
    });
  }, [sortedData]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <TableActionsMenu
          tableRef={tableRef}
          allRowsData={allRowsDataForExcel}
          filename={filename}
          sheetName="매체사별현황"
        />
      </Box>
      <TableContainer sx={{ backgroundColor: '#fff', borderRadius: 2, overflowX: 'auto' }}>
        <Table ref={tableRef} stickyHeader sx={{ borderCollapse: 'collapse', width: '100%' }}>
          <TableHead>
            <TableRow sx={{ height: 36 }}>
              <TableCell sx={{ fontWeight: 'bold', px: '15px', fontSize: '0.8rem' }}>
                <TableSortLabel active direction={sortDirection} onClick={toggleSort}>
                  날짜
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', px: '15px', fontSize: '0.8rem' }}>
                가장 많이 참여한 콘텐츠
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', px: '15px', fontSize: '0.8rem' }}>
                참여 유저 수
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', px: '15px', fontSize: '0.8rem' }}>
                매체사
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {paged.map((day) => (
              <Fragment key={day.date}>
                {/* 날짜별 요약 행 */}
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>{day.date}</TableCell>
                  <TableCell align="right">
                    {day.title}
                    <Chip
                      label={`${day.reward}캐시`}
                      variant="outlined"
                      size="small"
                      color="primary"
                      sx={{ ml: 1, fontSize: '0.8rem', borderColor: '#0044D2', color: '#0044D2' }}
                    />
                  </TableCell>
                  <TableCell align="right">{day.count.toLocaleString()}</TableCell>
                  <TableCell align="right">
                    {networkNameMap[day.networkName as keyof typeof networkNameMap]}
                  </TableCell>
                </TableRow>
              </Fragment>
            ))}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={sortedData.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[rowsPerPage]}
        />
      </TableContainer>
    </Box>
  );
}
